import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { AuthProvider, buildMsalConfig } from "@optimgov/module-auth"
import { App } from "./App"
import "./styles/globals.css"
import "./i18n"
import { completeSuperadminRedirect, SUPERADMIN_PENDING_KEY } from "./pages/superadmin/useSuperadminAuth"

const rc = (window as any).__CONTRACTACIO_CONFIG__

const msalConfig = buildMsalConfig({
	clientId: rc?.msalClientId ?? import.meta.env.VITE_ENTRA_CLIENT_ID,
	tenantId: rc?.msalTenantId ?? import.meta.env.VITE_ENTRA_TENANT_ID,
	hubUrl: rc?.hubUrl ?? import.meta.env.VITE_HUB_URL ?? "https://app.optimgov.es",
	apiScopes: [rc?.apiScope ?? import.meta.env.VITE_API_SCOPE ?? `api://${rc?.msalClientId ?? import.meta.env.VITE_ENTRA_CLIENT_ID}/access`],
	authority: rc?.msalAuthority ?? import.meta.env.VITE_AUTHORITY,
	knownAuthorities: (rc?.msalKnownAuthorities ?? import.meta.env.VITE_KNOWN_AUTHORITIES)?.split(","),
	redirectUri: rc?.msalRedirectUri,
})

const authMode = (rc?.authMode ?? ((import.meta.env.VITE_LOCAL_MODE === "true" || import.meta.env.VITE_AUTH_MODE === "mock")
	? "mock"
	: "msal")) as "mock" | "msal"

async function boot() {
	// If a SUPERADMIN redirect login is pending, finish it with the isolated
	// (sessionStorage) MSAL instance BEFORE the tenant provider mounts, so the two
	// instances never race to consume each other's redirect response. On success
	// bounce to /superadmin (the redirect lands on the bare origin).
	if (authMode === "msal" && window.sessionStorage.getItem(SUPERADMIN_PENDING_KEY)) {
		try {
			const account = await completeSuperadminRedirect()
			if (account) window.history.replaceState({}, "", "/superadmin")
		} catch (err) {
			console.warn("[superadmin] redirect completion failed:", err)
		} finally {
			window.sessionStorage.removeItem(SUPERADMIN_PENDING_KEY)
		}
	}

	createRoot(document.getElementById("root")!).render(
		<StrictMode>
			<AuthProvider mode={authMode} context="module" msalConfig={msalConfig}>
				<App />
			</AuthProvider>
		</StrictMode>,
	)
}

// MSAL's silent token renewal loads the redirectUri (this whole SPA) in a
// hidden iframe. Booting the app there invokes MSAL APIs inside the iframe,
// which consumes the auth response and fails every renewal with
// block_iframe_reload; staying inert fails too (timed_out), because MSAL v5
// delivers the response via BroadcastChannel, not by polling the iframe URL.
// Run only the redirect bridge in the iframe (msal-browser docs
// login-user.md#redirecturi-considerations) and skip the app entirely.
const isMsalAuthIframe = window.self !== window.parent
	&& /[#&](code|error|state)=/.test(window.location.hash)

if (isMsalAuthIframe) {
	void import("@azure/msal-browser/redirect-bridge").then(({ broadcastResponseToMainFrame }) =>
		broadcastResponseToMainFrame(),
	)
} else {
	void boot()
}
